import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import type { DashboardPipeline } from '@/api/dashboard';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { cn } from '@/lib/cn';
import { candidatesUrl } from '@/pages/Candidates/candidateFilters';
import { type } from '@/theme/typography';

import { NO_ANIMATION, segmentStroke, statusDotClass, statusFill } from './chartStyle';

interface Props {
  pipeline: DashboardPipeline;
}

/**
 * `DESIGN.md` §6.2, Section 3 — Pipeline.
 *
 * ⚠️ **A current snapshot that does not honour `?period=`** — state, not flow. A status
 * distribution over a window is ambiguous, so this is the live distribution of the considerations
 * that exist now.
 *
 * ⚠️ **How a reader knows that is the subtitle under the page title, and this docblock has argued it
 * from two devices that no longer exist.** It first read *"the note under the eyebrow says so"* while that
 * note was deleted in the same commit (TASK-129); it then argued from **position** — this section
 * rendering above the *Analysis* boundary, where the selector visibly did not reach it. The boundary is
 * `reversed` (§6.2, 2026-08-12, TASK-148/TASK-150) and this section's position no longer says anything
 * about scope, so what carries the claim now is one sentence in the header: *"Period affects analysis
 * below. Outstanding feedback is always current."* That is why §6.2 calls it load-bearing and why
 * Section 3's own retired note stays retired — the subtitle says it once for the whole screen instead of
 * section by section.
 *
 * ⚠️ **Not a funnel, deliberately.** A funnel asserts that each stage flows into the next, and
 * `REJECTED` branches off every stage — drawing it as the last step of a funnel would be a chart
 * that lies about the process while looking authoritative. It is one stacked bar of the whole
 * tenant, and the five statuses are listed beneath it with their counts.
 *
 * All five statuses are always present and **a zero renders as `0`**, never as an absent segment:
 * the API's list comes from the model's own choices, so a status nobody is in is still a row here.
 *
 * §9: the bar is decorative and hidden from assistive technology — the list beneath it carries
 * every number, each with its status word (§3, "colour never carries meaning alone").
 *
 * **Each segment links to Candidates pre-filtered by that status** (§6.2: *"Segments link to
 * Candidates pre-filtered by that status"*), because the pipeline is state and the way to act on a
 * state is to see who is in it. Sprint 8 shipped the segments as plain text on purpose — `/candidates`
 * was a *"not built yet"* placeholder then, and a link to one teaches the reader that the row is not
 * clickable — and the deferral was recorded in the board's Backlog for the sprint that builds the
 * destination. TASK-068 is that sprint's payment of it.
 *
 * Three things about the link, each a decision rather than a detail:
 *
 *  * **The address is built by `candidatesUrl()` and is never spelled out here.** One module owns the
 *    filter contract (`candidateFilters.ts`), including that `status` repeats rather than being
 *    bracketed — django-ninja ignores `status[]=NEW` and answers with **every** row while the chip
 *    claims a filter is applied, which is §6.3's own named failure.
 *  * **One status, not five and not the default.** The destination's active-only default is the
 *    *absence* of `status`, so `?status=HIRED` is what makes a segment's own status the applied filter
 *    — the two statuses the default hides are exactly the two a reader is most likely to click.
 *  * **The `?period=` never travels.** This section is a current snapshot and does not honour the
 *    period (above); carrying one into an address that has no such parameter would imply it did.
 *
 * **A zero segment links too.** §6.2 makes a zero information, the destination answers an empty filter
 * with a designed state (*"No candidates match these filters."* and *Clear filters*), and a list where
 * some segments are clickable and others silently are not is the same lesson Sprint 8 refused to
 * teach. "Nobody is in `OFFER`" is a fact a reader wants to check, not one to make unclickable.
 */
export function PipelineSection({ pipeline }: Props) {
  const { t } = useTranslation(['dashboard', 'components']);
  const segments = pipeline.segments;
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);

  // One row, one bar per status, stacked — the shape recharts needs for a single stacked bar.
  const row: Record<string, number> = {};
  for (const segment of segments) row[segment.status] = segment.count;

  const summary = segments
    .map((segment) => t('pipeline.segment', { status: segment.status, count: segment.count }))
    .join(', ');

  return (
    <section className="mt-10" aria-label={t('pipeline.title')}>
      {/*
        ⚠️ **No trailing count on this eyebrow** (§6.2, from the reference): the counts *are* the
        legend, and a total above a bar that is already five numbers is the same number twice.
      */}
      <Eyebrow>{t('pipeline.title')}</Eyebrow>

      <figure aria-label={t('pipeline.summary', { summary })}>
        {/*
          ⚠️ **32 px, against the 9 px measured on the deployed screen** (§6.2's *Measured sizes*,
          2026-08-12). *"Full width"* was specified and a height never was, so the bar shipped at
          whatever its first container was — and at 9 px five hairline-separated segments cannot be
          told apart, which makes the section a divider with a legend under it rather than a chart.
          Both numbers move together: the outer box is what a browser measures, the
          `ResponsiveContainer` is what recharts draws inside it.
        */}
        <div aria-hidden className="h-8 w-full overflow-hidden rounded border border-rule">
          {total > 0 && (
            <ResponsiveContainer width="100%" height={32}>
              <BarChart
                layout="vertical"
                data={[row]}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              >
                <XAxis type="number" domain={[0, total]} hide />
                <YAxis type="category" hide />
                {segments.map((segment) => (
                  <Bar
                    key={segment.status}
                    dataKey={segment.status}
                    stackId="pipeline"
                    fill={statusFill[segment.status]}
                    stroke={segmentStroke}
                    strokeWidth={1}
                    isAnimationActive={NO_ANIMATION}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <ul className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
          {segments.map((segment) => (
            <li key={segment.status}>
              {/*
                §9: the link says what it does, and it says the number too. Its accessible name is
                the status word — the same string `StatusDot` shows, so the visible label is part of
                the name — plus the count and where activating it goes. Without the label a screen
                reader announces "link, New 0", which is the series read out without its purpose.
              */}
              <Link
                to={candidatesUrl({ status: [segment.status] })}
                aria-label={t('pipeline.segmentLink', {
                  status: t(`components:status.${segment.status}`),
                  count: segment.count,
                })}
                className="flex items-center gap-2 rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {/*
                  Dot, label, count — **one treatment five times** (§6.2, 2026-08-12). The dot is
                  decoration: the word beside it is what names the series, so it is `aria-hidden` and
                  §3's *"colour never carries meaning alone"* holds.
                */}
                <span
                  aria-hidden
                  className={cn('size-2 shrink-0 rounded-full', statusDotClass[segment.status])}
                />
                <span className="text-sm text-ink-900">
                  {t(`components:status.${segment.status}`)}
                </span>
                <span className={`${type.numeral} text-sm text-ink-500`}>{segment.count}</span>
              </Link>
            </li>
          ))}
        </ul>
      </figure>
    </section>
  );
}
